// Общие функции ИИ-генератора: склонения, статус навыка в учебном плане, разбор текста плана.

export const plural = (n, [one, few, many]) => {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b > 1 && b < 5) return few
  if (b === 1) return one
  return many
}

export const pluralN = (n, forms) => `${n} ${plural(n, forms)}`

// Статус навыка из учебного плана (trajectory_skill): курс готов / есть планы / пусто.
export const skillStatus = (skill) => {
  const courses = skill?.generated_quests_count || (skill?.has_generated_quest ? 1 : 0)
  const plans = skill?.education_plans_count || 0
  if (courses > 0)
    return {
      kind: 'course',
      dot: 'var(--g-ok)',
      meta: courses > 1 ? pluralN(courses, ['курс', 'курса', 'курсов']) : 'курс готов',
    }
  if (plans > 0) return { kind: 'plans', dot: 'var(--g-accent)', meta: pluralN(plans, ['план', 'плана', 'планов']) }
  return { kind: 'none', dot: 'var(--g-miss-text)', meta: 'нет курса' }
}

export const sameName = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase()

// Текст плана от ИИ: «1. Модуль:\n— тема\n— тема\n2. ...». Возвращает модули с темами.
export const parsePlan = (text) => {
  const modules = []
  ;(text || '').split('\n').forEach((raw) => {
    const line = raw.trim()
    if (!line) return
    const m = line.match(/^(\d+)[.)]\s*(.+?)\s*:?\s*$/)
    if (m) {
      modules.push({ title: m[2].replace(/[*#]/g, '').trim(), topics: [] })
      return
    }
    const t = line.replace(/^[—–\-•*]\s*/, '').trim()
    if (t && modules.length) modules[modules.length - 1].topics.push(t)
  })
  return modules
}

// Средняя зарплата вакансии в рублях (для диаграммы и подписей).
// Зарплата вакансии как { from, to, currency }. hh отдаёт объект, trudvsem — текст
// («от 120000 до 200000», «120000 - 150000», «до 90000», «100000»); без разбора текста
// вакансии trudvsem выпадали из диаграммы зарплат.
export const salaryRange = (vacancy) => {
  const s = vacancy?.salary
  if (!s) return null
  if (typeof s === 'object') return s
  const text = String(s).toLowerCase()
  const nums = (text.replace(/(\d)[\s\u00a0]+(?=\d)/g, '$1').match(/\d+(?:[.,]\d+)?/g) || [])
    .map((n) => Number(n.replace(',', '.')))
    .filter((n) => n > 0)
  if (!nums.length) return null
  if (nums.length >= 2) return { from: nums[0], to: nums[1] }
  if (/^\s*до/.test(text)) return { to: nums[0] }
  return { from: nums[0] }
}

// Примерный курс к рублю для валют hh.ru (порядок величин важнее точности)
const RUB_RATE = {
  RUR: 1,
  RUB: 1,
  USD: 90,
  EUR: 100,
  KZT: 0.18,
  BYR: 28,
  BYN: 28,
  UAH: 2.2,
  UZS: 0.0072,
  KGS: 1.05,
  AZN: 53,
  GEL: 33,
}

export const salaryAverage = (vacancy) => {
  const s = salaryRange(vacancy)
  if (!s) return null
  const from = Number(s.from) || 0
  const to = Number(s.to) || 0
  let avg = from && to ? (from + to) / 2 : to || from
  if (!avg) return null
  // в рубли по примерному курсу; неизвестная валюта — без суммы (раньше всё умножалось на 90,
  // и вакансия в тенге/сумах становилась «миллионной» и сплющивала диаграмму)
  const rate = RUB_RATE[(s.currency || 'RUR').toUpperCase()]
  if (!rate) return null
  return Math.round(avg * rate)
}

export const formatRub = (n) => `${Math.round(n).toLocaleString('ru-RU')} ₽`

const CUR_SIGN = { RUR: '₽', RUB: '₽', USD: '$', EUR: '€', KZT: '₸', UZS: 'сум', BYR: 'Br', BYN: 'Br', UAH: '₴' }

export const salaryLabel = (vacancy) => {
  const s = salaryRange(vacancy)
  if (!s || (!s.from && !s.to)) return 'з/п не указана'
  const code = (s.currency || 'RUR').toUpperCase()
  const cur = CUR_SIGN[code] || code
  const f = (n) => Number(n).toLocaleString('ru-RU')
  let text = `до ${f(s.to)} ${cur}`
  if (s.from && s.to) text = `${f(s.from)}–${f(s.to)} ${cur}`
  else if (s.from) text = `от ${f(s.from)} ${cur}`
  // зарплата в тенге, сумах и т.п. — рядом сумма в рублях по примерному курсу
  const rub = cur !== '₽' && salaryAverage(vacancy)
  return rub ? `${text} (≈ ${formatRub(rub)})` : text
}
